// components/Enrollment/StudentCourseDetail.js
// Student-facing course detail + batch selection + apply/pay
//
// Layout follows vc_cms theme-2's course detail (themes/theme-2/pages/CourseDetail.js):
// the course banner sits *behind* the hero content under a dark gradient wash,
// then a content column + sticky enrolment card. Adapted for the LMS shell —
// the hero is a rounded card rather than full-bleed, and the detail sections are
// tabs instead of theme-2's stacked blocks.
//
// Each fact has exactly one home, so nothing is stated twice:
//   • specs (duration / mode / batches) → the hero meta row
//   • what's included                   → the sidebar list
//   • the description                   → the About tab
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Spinner, Modal } from 'react-bootstrap';
import {
  Clock, Layers, Users, Calendar, CheckCircle2, MapPin,
  Info, ShieldCheck, CreditCard, ArrowRight, AlertCircle, XCircle,
  Play, BookOpen, Sparkles, PauseCircle, Send, QrCode,
} from 'lucide-react';
import { AppButton, AppBadge, BackButton, EmptyState, Tabs, TabsList, TabsTrigger, TabsContent } from '../ui';
import CommonToaster from '../Common/CommonToaster';
import ProtectedVideoPlayer from '../Common/ProtectedVideoPlayer';
import QrPayModal from '../QrPayments/QrPayModal';
import QrPayCard from '../QrPayments/QrPayCard';
import { getCatalogCourseDetail, applyToCourse, verifyEnrollmentPayment } from '../../utils/enrollmentApi';
import { getQrConfig, courseAcceptsQr } from '../../utils/qrPaymentApi';
import apiClient from '../../utils/apiClient';
import SyllabusView from '../Syllabus/SyllabusView';
import useRazorpay from '../../hooks/useRazorpay';
import RichText, { hasRichContent, isHtml } from '../Common/RichText';
// Styles are page-owned (`enr-*`). Deliberately NOT importing the self-paced
// stylesheet — this page used to borrow its `spd-*` classes, which meant any
// tweak here shifted the self-paced course pages too.
import './_enrollment-pages.scss';

const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

/**
 * Caps a long course description at a readable height and scrolls inside the
 * card, so a 40-paragraph description can't push the batch picker off-screen.
 *
 * The bottom fade is driven by measurement rather than always-on: it appears
 * only while there is genuinely more to read, otherwise a short description
 * would look truncated when it isn't. The cap is lifted on phones (see
 * `.enr-prose-scroll` in the stylesheet) — a nested scroll area inside a
 * scrolling page is worse than just letting the page grow on touch.
 */
const ScrollableProse = ({ children, label }) => {
  const scrollRef = useRef(null);
  const [more, setMore] = useState(false);

  const measure = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    // 4px slack absorbs sub-pixel rounding at fractional zoom levels.
    const hasOverflow = el.scrollHeight - el.clientHeight > 4;
    const atEnd = el.scrollTop + el.clientHeight >= el.scrollHeight - 4;
    const next = hasOverflow && !atEnd;
    // Guarded so the scroll handler doesn't re-render on every tick.
    setMore((prev) => (prev === next ? prev : next));
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    measure();
    // Observe the content too — late-loading images change scrollHeight without
    // resizing the container itself.
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    if (el.firstElementChild) ro.observe(el.firstElementChild);
    return () => ro.disconnect();
  }, [measure]);

  return (
    <div className={`enr-prose-wrap${more ? ' has-more' : ''}`}>
      <div
        className="enr-prose-scroll"
        ref={scrollRef}
        onScroll={measure}
        tabIndex={0}
        role="region"
        aria-label={label}
      >
        {children}
      </div>
    </div>
  );
};

// Parse newline-separated strings into arrays
const toList = (v) => (Array.isArray(v) ? v : typeof v === 'string' ? v.split('\n').map(s => s.trim()).filter(Boolean) : []);

// Rich-text fields come from the Quill editor as HTML; legacy rows hold
// newline-separated plain text. Only the plain form becomes a bullet list —
// HTML is handed to <RichText> so the markup renders instead of showing as tags.
const toPlainList = (v) => (isHtml(v) ? [] : toList(v));

export default function StudentCourseDetail() {
  const { id: courseId } = useParams();
  const navigate = useNavigate();
  const { openCheckout, loading: rzpLoading } = useRazorpay();

  const [course, setCourse] = useState(null);
  const [batches, setBatches] = useState([]);
  const [existingRequest, setExistingRequest] = useState(null);
  const [paymentEnabled, setPaymentEnabled] = useState(false);
  const [allowRequestToJoin, setAllowRequestToJoin] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);
  // Which button is mid-flight, so only that one shows a spinner.
  const [applyingMode, setApplyingMode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [previewVideos, setPreviewVideos] = useState([]);
  const [syllabusTemplate, setSyllabusTemplate] = useState(null);
  const [playingVideo, setPlayingVideo] = useState(null);
  const [streamUrl, setStreamUrl] = useState(null);
  const [streamLoading, setStreamLoading] = useState(false);
  // UPI QR: the institute's set-up (404/403 → off) and whether the dialog is open.
  const [qrConfig, setQrConfig] = useState(null);
  const [qrOpen, setQrOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [res, qrRes] = await Promise.all([
        getCatalogCourseDetail(courseId),
        getQrConfig().catch(() => null),
      ]);
      setQrConfig(qrRes?.data?.data || { enabled: false });
      const d = res.data?.data || res.data;
      setCourse(d.course);
      setBatches(d.batches || []);
      setExistingRequest(d.existingRequest || null);
      setPaymentEnabled(!!d.paymentEnabled);
      setAllowRequestToJoin(!!d.allowRequestToJoin);
      // Auto-select first selectable batch
      const selectable = (d.batches || []).filter(b => b.status === 'Upcoming' || b.status === 'Ongoing');
      if (selectable.length === 1) setSelectedBatch(selectable[0]._id);

      // Fetch preview videos (public endpoint, no auth required)
      try {
        const pvRes = await apiClient.get(`/materials/course/${courseId}/previews`);
        if (pvRes.data?.success) setPreviewVideos(pvRes.data.data || []);
      } catch {}

      // Fetch the active structured syllabus template (day-wise or module-wise).
      // 404 simply means no template — fall back to the free-text syllabus field.
      try {
        const tplRes = await apiClient.get(`/syllabus/template/${courseId}`);
        if (tplRes.data?.template) setSyllabusTemplate(tplRes.data.template);
      } catch {}
    } catch {
      setCourse(null);
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  useEffect(() => { load(); }, [load]);

  // Use feeAmount from backend (pre-parsed), fallback to parseFloat
  const feeAmount = course ? (course.feeAmount != null ? course.feeAmount : (parseFloat(String(course.fees || '0').replace(/[^0-9.]/g, '')) || 0)) : 0;
  const isFree = feeAmount === 0;
  // Pay-by-QR: the institute has a QR switched on, and this course has not
  // opted out (courses follow the institute). Access waits for an admin to
  // approve the payment.
  const qrAvailable = !isFree && courseAcceptsQr(course) && !!qrConfig?.enabled;

  // Filter batches: only show if endDate is today or future (status field may be stale)
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const selectableBatches = batches.filter(b =>
    (b.status === 'Upcoming' || b.status === 'Ongoing') &&
    (!b.endDate || new Date(b.endDate) >= now)
  );
  const hasSelectableBatches = selectableBatches.length > 0;

  // Parse text content
  const highlights = toPlainList(course?.highlights);
  const prerequisites = toPlainList(course?.prerequisites);
  const syllabus = toPlainList(course?.syllabus);

  /**
   * Both enrolment routes, because they only diverge at the response.
   *
   * @param {'pay'|'request'} mode  'pay' opens Razorpay; 'request' files an
   *   application for an admin to decide, and never touches the gateway.
   */
  const handleApply = async (mode = 'pay') => {
    if (!course) return;
    if (hasSelectableBatches && !selectedBatch) {
      setToast({ show: true, message: 'Please select a batch first.', type: 'error' });
      return;
    }

    setApplying(true);
    setApplyingMode(mode);
    const finish = () => { setApplying(false); setApplyingMode(null); };
    try {
      const res = await applyToCourse({
        courseId: course._id,
        batchId: selectedBatch || null,
        mode,
      });
      const data = res.data?.data || res.data;

      // Free course, an explicit "request to join", or no usable gateway — in
      // all three the server already filed the request and there is nothing to
      // pay for right now.
      if (isFree || mode === 'request' || !data.orderId) {
        setToast({ show: true, message: res.data?.message || 'Request submitted!', type: 'success' });
        setExistingRequest(data.request || data);
        finish();
        return;
      }

      // Paid → open Razorpay
      openCheckout(
        {
          razorpayKeyId: data.razorpayKeyId,
          order: { id: data.orderId, amount: Math.round(data.amount * 100), currency: data.currency || 'INR' },
          description: `Course enrollment: ${course.name || course.title}`,
        },
        async (response) => {
          try {
            await verifyEnrollmentPayment({
              courseId: course._id,
              razorpayOrderId: response.razorpay_order_id,
              razorpayPaymentId: response.razorpay_payment_id,
              razorpaySignature: response.razorpay_signature,
            });
            setToast({ show: true, message: 'Payment successful! You are now enrolled.', type: 'success' });
            setExistingRequest({ status: 'paid' });
          } catch {
            setToast({ show: true, message: 'Payment verification failed.', type: 'error' });
          }
          finish();
        },
        (err) => {
          setToast({ show: true, message: err?.reason || 'Payment cancelled.', type: 'error' });
          finish();
        },
      );
    } catch (err) {
      setToast({ show: true, message: err?.response?.data?.message || 'Failed to apply.', type: 'error' });
      finish();
    }
  };

  /** Open the QR dialog — the same batch rule as paying online. */
  const openQr = () => {
    if (hasSelectableBatches && !selectedBatch) {
      setToast({ show: true, message: 'Please select a batch first.', type: 'error' });
      return;
    }
    setQrOpen(true);
  };

  const onQrSubmitted = (_payment, message) => {
    setQrOpen(false);
    setExistingRequest({ status: 'pending', enrollmentMode: 'qr', batchId: selectedBatch || null });
    setToast({ show: true, message: message || 'Payment sent for verification.', type: 'success' });
  };

  /* Status banner config */
  const STATUS_CONFIG = {
    pending: {
      icon: <Clock size={16} />,
      label: existingRequest?.enrollmentMode === 'qr'
        ? 'Payment sent — your institute is checking it. You\'ll get access as soon as it\'s approved.'
        : existingRequest?.batchId
          ? 'Your request is pending admin approval.'
          : 'Application received! You\'ll be assigned to the next available batch.',
    },
    approved: {
      icon: <CheckCircle2 size={16} />,
      label: existingRequest?.alreadyEnrolled
        ? 'You are already enrolled in this course.'
        : 'You are enrolled in this course!',
    },
    on_hold: {
      icon: <PauseCircle size={16} />,
      label: existingRequest?.holdReason
        ? `Your request is on hold — ${existingRequest.holdReason}`
        : 'Your request has been placed on hold. The team will follow up with you.',
    },
    paid: { icon: <CheckCircle2 size={16} />, label: 'Payment received — you are enrolled!' },
    rejected: {
      icon: <XCircle size={16} />,
      label: existingRequest?.rejectReason
        ? `Your request was declined — ${existingRequest.rejectReason}`
        : 'Your request was declined.',
    },
  };

  const isAlreadyEnrolled = !!(existingRequest?.alreadyEnrolled);

  /* Primary CTA label — the pay-now route (or the only route, when free). */
  const getCtaLabel = () => {
    if (applyingMode === 'pay' || rzpLoading) return <><Spinner size="sm" animation="border" /> Processing…</>;
    if (isFree) return hasSelectableBatches ? 'Enroll Now — Free' : 'Apply for this Course — Free';
    // No usable gateway: the single button IS the request, so say so rather
    // than promising an "apply" that behaves differently from the one below.
    if (!paymentEnabled) return <>Request to Join <Send size={15} /></>;
    return <>Pay ₹{feeAmount.toLocaleString('en-IN')} & Enroll <ArrowRight size={15} /></>;
  };

  // With no gateway but a QR, paying by QR is the primary route.
  const qrIsPrimary = !isFree && !paymentEnabled && qrAvailable;

  // Skeleton mirrors the real layout (hero → content → sticky card) so nothing
  // jumps when the data lands.
  if (loading) return (
    <div className="enr-detail">
      <div className="enr-detail__skeleton">
        <div className="shimmer enr-skel enr-skel--hero" />
        <div className="enr-detail__layout">
          <div className="enr-detail__content">
            <div className="shimmer enr-skel enr-skel--batches" />
            <div className="shimmer enr-skel enr-skel--tabs" />
          </div>
          <div className="shimmer enr-skel enr-skel--aside" />
        </div>
      </div>
    </div>
  );
  if (!course) return <div className="enr-detail"><EmptyState title="Course not found" description="This course may no longer be available." /></div>;

  const statusCfg = existingRequest ? STATUS_CONFIG[existingRequest.status] : null;

  const formatDuration = (min) => {
    if (!min || min <= 0) return null;
    if (min >= 60) return `${Math.floor(min / 60)}h ${Math.round(min % 60)}m`;
    return `${Math.round(min)}m`;
  };

  const handlePlayPreview = async (video) => {
    setPlayingVideo(video);
    setStreamUrl(null);
    setStreamLoading(true);
    try {
      const res = await apiClient.get(`/materials/${video._id}/stream`);
      if (res.data?.success) setStreamUrl(res.data.data.url);
    } catch {
      setToast({ show: true, message: 'Failed to load video.', type: 'error' });
      setPlayingVideo(null);
    }
    setStreamLoading(false);
  };

  // Course carries two distinct images: `banner` is the wide (1920×480) detail
  // artwork and `thumbnail` is the card image. Each is used for its own job —
  // banner behind the hero, thumbnail in the sidebar card — so neither appears
  // twice. No cross-fallback for that reason: a missing banner drops to the
  // brand-tinted panel rather than reusing the thumbnail.
  const banner = course.bannerUrl || course.banner;
  const thumbnail = course.thumbnailUrl || course.thumbnail || course.image;
  const isEnrolledState = isAlreadyEnrolled || existingRequest?.status === 'paid' || existingRequest?.status === 'approved';

  // The dark wash keeps white text legible over arbitrary artwork and renders
  // identically in light and dark theme. encodeURI stops a stray quote in the
  // URL from breaking out of the url() token.
  const heroStyle = banner
    ? { backgroundImage: `linear-gradient(105deg, rgba(9,13,24,0.94) 0%, rgba(15,23,42,0.78) 52%, hsl(var(--primary) / 0.5) 100%), url("${encodeURI(banner)}")` }
    : undefined;

  const sectionIcon = (tone, icon) => (
    <span className={`enr-section__icon enr-section__icon--${tone}`}>{icon}</span>
  );

  return (
    <div className="enr-detail">
      <div className="enr-detail__topbar">
        <BackButton onClick={() => navigate('/s/courses')} />
      </div>

      {/* ── Hero — banner behind the copy (vc_cms theme-2 pattern) ── */}
      <header className={`enr-hero${banner ? ' enr-hero--banner' : ''}`} style={heroStyle}>
        <div className="enr-hero__body">
          <div className="enr-hero__eyebrow">
            {course.shortCode && <span className="enr-hero__code">{course.shortCode}</span>}
            {/* Factual claim only — mirrors the "Instructor-led batch training"
                already asserted in the sidebar list. */}
            <span className="enr-hero__kicker"><Sparkles size={12} /> Instructor-led course</span>
            {isEnrolledState && (
              <span className="enr-hero__enrolled"><CheckCircle2 size={12} /> Enrolled</span>
            )}
          </div>

          <h1 className="enr-hero__title">{course.name}</h1>

          {/* The single home for the course specs — not repeated in the sidebar.
              Description is deliberately absent: it lives in the About tab in
              full, formatted form rather than as a truncated lead here. */}
          <div className="enr-hero__meta">
            {course.duration && <span><Clock size={14} /> {course.duration}</span>}
            {course.mode && <span><MapPin size={14} /> {course.mode}</span>}
            <span>
              <Calendar size={14} />
              {hasSelectableBatches
                ? `${selectableBatches.length} batch${selectableBatches.length !== 1 ? 'es' : ''} available`
                : 'Next batch starting soon'}
            </span>
            {previewVideos.length > 0 && (
              <span className="enr-hero__chip">
                <Play size={13} /> {previewVideos.length} free preview{previewVideos.length !== 1 ? 's' : ''}
              </span>
            )}
          </div>
        </div>
      </header>

      {/* Two-column shell: scrolling content + sticky enrolment card. On narrow
          screens the card comes first so price/CTA are visible before the copy. */}
      <div className="enr-detail__layout">
        <div className="enr-detail__content">

          {/* ── Batch Selection ───────────────────────────────── */}
          {hasSelectableBatches && !existingRequest && !isAlreadyEnrolled && (
            <section className="enr-section">
              <h2 className="enr-section__title">
                {sectionIcon('amber', <Calendar size={15} />)} Choose a batch
                <span className="enr-section__count">{selectableBatches.length} available</span>
              </h2>
              <div className="enr-batches__list">
                {selectableBatches.map(b => {
                  const seatsLeft = b.maxStudents ? Math.max(b.maxStudents - (b.strength || 0), 0) : null;
                  const isSelected = selectedBatch === b._id;
                  const isFull = seatsLeft === 0;
                  // Capacity bar — only when the batch actually declares a cap.
                  const fillPct = b.maxStudents
                    ? Math.min(100, Math.round(((b.strength || 0) / b.maxStudents) * 100))
                    : null;
                  const almostFull = fillPct !== null && fillPct >= 80 && !isFull;

                  return (
                    <div
                      key={b._id}
                      className={`enr-batch-card${isSelected ? ' enr-batch-card--selected' : ''}${isFull ? ' enr-batch-card--full' : ''}`}
                      onClick={() => !isFull && setSelectedBatch(b._id)}
                      role="radio"
                      aria-checked={isSelected}
                      aria-disabled={isFull}
                      tabIndex={isFull ? -1 : 0}
                      onKeyDown={(e) => {
                        if (isFull) return;
                        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedBatch(b._id); }
                      }}
                    >
                      <div className="enr-batch-card__radio">
                        <div className={`enr-batch-card__radio-dot${isSelected ? ' enr-batch-card__radio-dot--active' : ''}`} />
                      </div>
                      <div className="enr-batch-card__info">
                        <div className="enr-batch-card__name">
                          {b.name}
                          <AppBadge color={b.status === 'Upcoming' ? 'primary' : 'emerald'} size="sm">{b.status}</AppBadge>
                          {almostFull && <AppBadge color="amber" size="sm">Filling fast</AppBadge>}
                        </div>
                        <div className="enr-batch-card__meta">
                          <span><Calendar size={13} /> {formatDate(b.startDate)} — {formatDate(b.endDate)}</span>
                          {b.startTime && <span><Clock size={13} /> {b.startTime}</span>}
                          {b.mode && <span><MapPin size={13} /> {b.mode}</span>}
                          {b.batchType && <span><Layers size={13} /> {b.batchType}</span>}
                        </div>
                      </div>
                      <div className="enr-batch-card__seats">
                        {isFull ? (
                          <AppBadge color="rose" size="sm">Full</AppBadge>
                        ) : seatsLeft !== null ? (
                          <>
                            <span className="enr-batch-card__seats-text">
                              <Users size={13} /> {seatsLeft} seat{seatsLeft !== 1 ? 's' : ''} left
                            </span>
                            <div className="enr-batch-card__capacity" aria-hidden>
                              <span
                                className={`enr-batch-card__capacity-fill${almostFull ? ' is-tight' : ''}`}
                                style={{ width: `${fillPct}%` }}
                              />
                            </div>
                          </>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {/* ── Tabbed Content ──────────────────────────────────── */}
          {(() => {
            const hasStructuredSyllabus = !!syllabusTemplate && (
              syllabusTemplate.syllabusType === 'module'
                ? (syllabusTemplate.modules || []).length > 0
                : (syllabusTemplate.days || []).length > 0
            );
            const hasAbout = hasRichContent(course?.description);
            const hasSyllabus = hasStructuredSyllabus || syllabus.length > 0 || hasRichContent(course?.syllabus);
            const hasPrereqs = prerequisites.length > 0 || hasRichContent(course?.prerequisites);
            const hasHighlights = highlights.length > 0 || hasRichContent(course?.highlights);
            const hasVideos = previewVideos.length > 0;
            if (!hasAbout && !hasSyllabus && !hasPrereqs && !hasHighlights && !hasVideos) return null;
            const defaultTab = hasAbout ? 'about'
              : hasSyllabus ? 'syllabus'
              : hasPrereqs ? 'prerequisites'
              : hasHighlights ? 'highlights' : 'videos';

            // Legacy newline-separated fields render as a bullet list that matches
            // the markers <RichText> paints for editor-authored HTML.
            const bulletList = (items) => (
              <ul className="enr-detail__bullets">
                {items.map((item, i) => <li key={i}>{item}</li>)}
              </ul>
            );

            // Outcomes read better as a two-column ticked grid than as bullets.
            const checkGrid = (items) => (
              <ul className="enr-checks">
                {items.map((item, i) => (
                  <li key={i} className="enr-checks__item">
                    <CheckCircle2 size={15} /> <span>{item}</span>
                  </li>
                ))}
              </ul>
            );

            // Rich text in a tab panel is capped and scrolls internally, so the
            // page doesn't jump height when switching between a two-line
            // prerequisites list and a 40-paragraph description.
            // `compact` keeps stored headings in proportion with the tab labels
            // and the rest of the UI instead of using RichText's editorial scale.
            const prosePanel = (value, label, modifier = '') => (
              <div className="enr-section__card enr-section__card--flush">
                <ScrollableProse label={label}>
                  <RichText compact value={value} className={`enr-detail__prose ${modifier}`.trim()} />
                </ScrollableProse>
              </div>
            );

            return (
              <section className="enr-section">
                <h2 className="enr-section__title">
                  {sectionIcon('sky', <BookOpen size={15} />)} Course details
                </h2>
                <Tabs defaultValue={defaultTab} className="enr-detail__tabs">
                  <TabsList>
                    {hasAbout && <TabsTrigger value="about">About</TabsTrigger>}
                    {hasSyllabus && <TabsTrigger value="syllabus">Syllabus</TabsTrigger>}
                    {hasPrereqs && <TabsTrigger value="prerequisites">Prerequisites</TabsTrigger>}
                    {hasHighlights && <TabsTrigger value="highlights">What you'll learn</TabsTrigger>}
                    {hasVideos && <TabsTrigger value="videos">Preview Course</TabsTrigger>}
                  </TabsList>

                  {hasAbout && (
                    <TabsContent value="about">
                      {prosePanel(course.description, 'Course description')}
                    </TabsContent>
                  )}

                  {hasSyllabus && (
                    <TabsContent value="syllabus">
                      {hasStructuredSyllabus ? (
                        <SyllabusView template={syllabusTemplate} />
                      ) : syllabus.length > 0 ? (
                        <div className="enr-section__card">{bulletList(syllabus)}</div>
                      ) : (
                        prosePanel(course.syllabus, 'Syllabus')
                      )}
                    </TabsContent>
                  )}

                  {hasPrereqs && (
                    <TabsContent value="prerequisites">
                      {prerequisites.length > 0
                        ? <div className="enr-section__card">{bulletList(prerequisites)}</div>
                        : prosePanel(course.prerequisites, 'Prerequisites')}
                    </TabsContent>
                  )}

                  {hasHighlights && (
                    <TabsContent value="highlights">
                      {highlights.length > 0
                        ? <div className="enr-section__card">{checkGrid(highlights)}</div>
                        : prosePanel(course.highlights, "What you'll learn", 'enr-detail__prose--checks')}
                    </TabsContent>
                  )}

                  {hasVideos && (
                    <TabsContent value="videos">
                      <div className="enr-preview-videos">
                        {previewVideos.map((v) => (
                          <div key={v._id} className="enr-preview-card" onClick={() => handlePlayPreview(v)}>
                            <div className="enr-preview-card__thumb">
                              {v.thumbnailUrl ? <img src={v.thumbnailUrl} alt="" /> : <div className="enr-preview-card__thumb-placeholder"><Play size={28} /></div>}
                              <div className="enr-preview-card__play-overlay"><Play size={24} /></div>
                              {formatDuration(v.duration) && (
                                <span className="enr-preview-card__duration">{formatDuration(v.duration)}</span>
                              )}
                            </div>
                            <div className="enr-preview-card__info">
                              <h4 className="enr-preview-card__title">{v.title}</h4>
                              {v.description && <p className="enr-preview-card__desc">{v.description}</p>}
                              <AppBadge color="emerald" size="sm">Free Preview</AppBadge>
                            </div>
                          </div>
                        ))}
                      </div>
                    </TabsContent>
                  )}
                </Tabs>
              </section>
            );
          })()}
        </div>

        {/* ── Sticky enrolment card ─────────────────────────────── */}
        <aside className="enr-detail__sidebar">
          <div className="enr-detail__sidebar-card">
            <div className="enr-detail__sidebar-body">
              {/* Card image — the `thumbnail` field, distinct from the hero banner. */}
              {thumbnail && (
                <div className="enr-detail__sidebar-thumb">
                  <img src={thumbnail} alt={course.name} loading="lazy" />
                </div>
              )}

              <div className="enr-detail__pricing">
                {isEnrolledState ? (
                  <span className="enr-detail__price enr-detail__price--enrolled">
                    <CheckCircle2 size={18} /> Enrolled
                  </span>
                ) : isFree ? (
                  <>
                    <span className="enr-detail__price enr-detail__price--free">Free</span>
                    <span className="enr-detail__price-note">No payment required</span>
                  </>
                ) : (
                  <>
                    <span className="enr-detail__price">₹{feeAmount.toLocaleString('en-IN')}</span>
                    <span className="enr-detail__price-note">One-time course fee</span>
                  </>
                )}
              </div>

              {statusCfg && (
                <div className={`enr-detail__status enr-detail__status--${existingRequest.status}`}>
                  {statusCfg.icon}
                  <span>{statusCfg.label}</span>
                </div>
              )}

              {/* No batch info — hide when already enrolled */}
              {!existingRequest && !isAlreadyEnrolled && !hasSelectableBatches && (
                <div className="enr-detail__status enr-detail__status--info">
                  <Info size={15} />
                  <span>No upcoming batches yet. Apply now — you'll be assigned when the next batch starts.</span>
                </div>
              )}

              {/* CTA — hide when already enrolled or a request is live.
                  A rejected request is re-appliable; pending / on_hold are not. */}
              {!isAlreadyEnrolled && (!existingRequest || existingRequest.status === 'rejected') ? (
                <div className="enr-detail__cta-group">
                  {/* No card gateway: the QR IS the way to pay, so show it, not a
                      button that hides it. */}
                  {qrIsPrimary ? (
                    <QrPayCard
                      layout="stack"
                      config={qrConfig}
                      amount={feeAmount}
                      blurb={`Scan with any UPI app and pay ₹${feeAmount.toLocaleString('en-IN')}, then upload the screenshot. You're enrolled as soon as the institute approves it.`}
                      ctaLabel="I've paid — upload proof"
                      onUpload={openQr}
                      disabled={applying}
                    />
                  ) : (
                    <AppButton
                      className="enr-detail__cta"
                      onClick={() => handleApply('pay')}
                      disabled={applying || rzpLoading}
                    >
                      {getCtaLabel()}
                    </AppButton>
                  )}

                  {/* Both online and QR: the card is primary, QR the alternative. */}
                  {paymentEnabled && qrAvailable && (
                    <AppButton
                      variant="secondary"
                      className="enr-detail__cta enr-detail__cta--secondary"
                      onClick={openQr}
                      disabled={applying || rzpLoading}
                    >
                      <QrCode size={15} /> Pay by UPI QR instead
                    </AppButton>
                  )}

                  {/* The pay-later route. Offered only when paying is actually
                      the alternative — with no gateway (and no QR) the primary
                      button is already the request, and a free course has
                      nothing to request. */}
                  {allowRequestToJoin && (paymentEnabled || qrAvailable) && (
                    <>
                      <div className="enr-detail__cta-or"><span>or</span></div>
                      <AppButton
                        variant="secondary"
                        className="enr-detail__cta enr-detail__cta--secondary"
                        onClick={() => handleApply('request')}
                        disabled={applying || rzpLoading}
                      >
                        {applyingMode === 'request'
                          ? <><Spinner size="sm" animation="border" /> Sending…</>
                          : <><Send size={15} /> Request to Join</>}
                      </AppButton>
                      <p className="enr-detail__cta-hint">
                        Apply without paying now — an admin will review your request
                        and confirm your seat. You'll be notified by email.
                      </p>
                    </>
                  )}
                </div>
              ) : null}

              {/* A live request is a dead end on this page — the student's next
                  question is "where does this stand", which lives elsewhere. */}
              {!isAlreadyEnrolled && ['pending', 'on_hold'].includes(existingRequest?.status) && (
                <AppButton
                  variant="secondary"
                  className="enr-detail__cta enr-detail__cta--secondary"
                  onClick={() => navigate('/s/courses/my-requests')}
                >
                  Track My Application <ArrowRight size={15} />
                </AppButton>
              )}

              {/* Enrolled students previously hit a dead end here — send them to
                  the course they just paid for. */}
              {isEnrolledState && (
                <AppButton className="enr-detail__cta" onClick={() => navigate('/s/learning')}>
                  Go to My Learning <ArrowRight size={15} />
                </AppButton>
              )}

              {/* Payment info for paid courses — hide when already enrolled */}
              {!existingRequest && !isAlreadyEnrolled && !isFree && (
                <div className="enr-detail__payment-info">
                  {paymentEnabled ? (
                    <span className="enr-detail__payment-tag enr-detail__payment-tag--online">
                      <CreditCard size={13} /> Secure online payment
                    </span>
                  ) : qrAvailable ? (
                    <span className="enr-detail__payment-tag enr-detail__payment-tag--manual">
                      <QrCode size={13} /> Access once your payment is verified
                    </span>
                  ) : (
                    <span className="enr-detail__payment-tag enr-detail__payment-tag--manual">
                      <AlertCircle size={13} /> Admin approval required
                    </span>
                  )}
                </div>
              )}

              {/* What's included — value props only. Duration and mode are specs
                  and live in the hero meta row, not here. */}
              <div className="enr-detail__includes">
                <span className="enr-detail__includes-title">This course includes</span>
                <ul className="enr-detail__perks">
                  <li><span className="enr-detail__perk-icon enr-detail__perk-icon--violet"><Users size={13} /></span> Instructor-led batch training</li>
                  <li><span className="enr-detail__perk-icon enr-detail__perk-icon--emerald"><ShieldCheck size={13} /></span> Structured curriculum</li>
                  {previewVideos.length > 0 && (
                    <li><span className="enr-detail__perk-icon enr-detail__perk-icon--sky"><Play size={13} /></span> Free preview lessons</li>
                  )}
                  {hasSelectableBatches && (
                    <li><span className="enr-detail__perk-icon enr-detail__perk-icon--amber"><Calendar size={13} /></span> Choose your batch timing</li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        </aside>
      </div>

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, show: false })}
      />

      {qrAvailable && (
        <QrPayModal
          show={qrOpen}
          onHide={() => setQrOpen(false)}
          purpose="batch_enrollment"
          courseId={course._id}
          batchId={selectedBatch || undefined}
          courseName={course.name || course.title}
          amount={feeAmount}
          config={qrConfig}
          onSubmitted={onQrSubmitted}
        />
      )}

      {/* ── Preview Video Player Modal ──────────────────────── */}
      <Modal
        show={!!playingVideo}
        onHide={() => { setPlayingVideo(null); setStreamUrl(null); }}
        centered
        size="lg"
        className="enr-video-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title className="fs-6 fw-semibold">{playingVideo?.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          {streamLoading ? (
            <div className="enr-video-modal__loading"><Spinner animation="border" variant="secondary" /></div>
          ) : streamUrl ? (
            <div className="enr-video-modal__player">
              <ProtectedVideoPlayer
                src={streamUrl}
                title={playingVideo?.title}
                autoPlay
                preload="auto"
              />
            </div>
          ) : null}
        </Modal.Body>
      </Modal>
    </div>
  );
}
